import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { BadgeCheck, LogOut, Menu, Search, Settings, Shield, Store, User, X, Zap } from "lucide-react";
import { useAuth } from "../lib/auth";
import { Wordmark } from "./Logo";
import { ThemeToggle } from "./ThemeProvider";

export default function Header() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const navLinks = [
    { to: "/about",                label: "About",     testid: "nav-about" },
    { to: "/download",             label: "Download",  testid: "nav-download" },
    { to: "/browse?type=plugin",   label: "Plugins",   testid: "nav-plugins" },
    { to: "/browse?type=workflow", label: "Workflows", testid: "nav-workflows" },
    { to: "/pricing",              label: "Pricing",   testid: "nav-pricing" },
    { to: "/publish",              label: "Publish",   testid: "nav-publish" },
    { to: "/docs",                 label: "Docs",      testid: "nav-docs" },
  ];

  return (
    <header className="border-b border-white/5 bg-bg/80 backdrop-blur-2xl sticky top-0 z-40 animate-fade-in-up" data-testid="site-header">
      {/* Sprint 2.16 — Header crowding fix.
          Before: nav switched to mobile hamburger at md (768px), which
          meant 7 nav-links + search + theme-toggle + 4 user-links +
          profile-pill + logout-btn all had to fit on screens as narrow
          as 768px. Result: text wrapped or overlapped between ~900-1100px.
          After:
            - desktop nav now requires lg (1024px) instead of md (768px)
              for the full layout — gives tablets a clean hamburger
            - main row gap dropped from 4/8 → 3/6 so even after 1024px
              there's breathing room before items touch
            - user-links cluster tightened from gap-4 → gap-3, profile
              pill name truncates at 100px instead of 120px
            - search button hidden on lg-only (kept on xl+) so the
              1024-1280px sweet spot has room for user-links */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 h-16 flex items-center gap-3 md:gap-6">
        <Link to="/" className="shrink-0 transition-transform hover:scale-105" data-testid="nav-home">
          <Wordmark size={20} />
        </Link>

        <nav className="hidden lg:flex items-center gap-5 font-display text-sm font-medium tracking-wide">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) => `transition-all duration-300 hover:text-text1 ${isActive ? "text-amber1 drop-shadow-md" : "text-text2"}`}
              data-testid={l.testid}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex-1" />

        <button
          onClick={() => nav("/browse")}
          className="hidden xl:flex items-center gap-2 border border-white/10 px-4 h-9 text-xs font-mono text-text2 hover:border-amber1 hover:text-text1 hover:bg-white/5 rounded-full transition-all duration-300"
          data-testid="nav-search-btn"
        >
          <Search size={14} /> search marketplace
        </button>

        <ThemeToggle className="hidden lg:inline-flex" />

        {!user ? (
          <div className="hidden sm:flex items-center gap-3">
            <Link to="/login" className="font-display text-sm font-medium px-4 h-9 flex items-center border border-white/10 text-text1 hover:border-white/30 hover:bg-white/5 rounded-full transition-all duration-300" data-testid="nav-login">Sign in</Link>
            <Link to="/signup" className="font-display text-sm font-medium px-4 h-9 flex items-center bg-amber1 text-bg hover:bg-amber2 shadow-[0_0_15px_rgba(244,201,93,0.3)] hover:shadow-[0_0_25px_rgba(244,201,93,0.5)] rounded-full transition-all duration-300" data-testid="nav-signup">Join</Link>
          </div>
        ) : (
          <div className="hidden lg:flex items-center gap-3">
            {user.is_admin && (
              <Link to="/moderation" className="font-display text-xs font-medium flex items-center gap-1.5 text-tier_demo_tx hover:text-amber1 transition-colors" data-testid="nav-moderation">
                <Shield size={14} /> Mod
              </Link>
            )}
            <Link to="/dashboard" className="font-display text-xs font-medium flex items-center gap-1.5 text-text2 hover:text-text1 transition-colors" data-testid="nav-dashboard">
              <Store size={14} /> My Shelf
            </Link>
            <Link to="/workflows" className="font-display text-xs font-medium flex items-center gap-1.5 text-text2 hover:text-text1 transition-colors" data-testid="nav-workflows">
              <Zap size={14} /> Workflows
            </Link>
            <Link to="/settings" className="font-display text-xs font-medium flex items-center gap-1.5 text-text2 hover:text-text1 transition-colors" data-testid="nav-settings">
              <Settings size={14} /> Settings
            </Link>
            <Link to={`/creator/${user.id}`} className="flex items-center gap-2 border border-white/10 px-3 h-9 hover:border-amber1 hover:bg-white/5 rounded-full transition-all duration-300" data-testid="nav-profile">
              <User size={14} className="text-text2" />
              <span className="font-mono text-xs max-w-[100px] truncate">{user.display_name}</span>
              {user.is_verified && <BadgeCheck size={14} className="text-amber1" />}
            </Link>
            <button onClick={() => { logout(); nav("/"); }} className="text-text2 hover:text-text1 transition-colors" data-testid="nav-logout" title="Sign out">
              <LogOut size={16} />
            </button>
          </div>
        )}

        <button
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden flex items-center justify-center w-10 h-10 border border-white/10 text-text1 hover:border-amber1 hover:bg-white/5 rounded-full transition-colors"
          aria-label={open ? "Close menu" : "Open menu"}
          data-testid="nav-menu-btn"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-surface2/95 backdrop-blur-xl animate-fade-in" data-testid="mobile-menu">
          <nav className="px-4 py-4 flex flex-col font-display text-base font-medium">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) => `py-3 border-b border-white/5 last:border-b-0 transition-colors ${isActive ? "text-amber1" : "text-text1 hover:text-text1"}`}
                data-testid={`mobile-${l.testid}`}
              >
                {l.label}
              </NavLink>
            ))}
            {/* Phase 20 — theme toggle inside mobile menu */}
            <div className="py-3 flex items-center justify-between border-t border-white/10 mt-1">
              <span className="text-text2 text-sm font-normal">Appearance</span>
              <ThemeToggle />
            </div>
          </nav>
          <div className="px-4 pb-6 pt-4 border-t border-white/10">
            {!user ? (
              <div className="flex flex-col gap-3">
                <Link to="/login" className="font-display text-sm font-medium px-4 h-11 flex items-center justify-center border border-white/10 text-text1 hover:bg-white/5 rounded-xl transition-colors" data-testid="mobile-nav-login">Sign in</Link>
                <Link to="/signup" className="font-display text-sm font-medium px-4 h-11 flex items-center justify-center bg-amber1 text-bg hover:bg-amber2 rounded-xl transition-colors" data-testid="mobile-nav-signup">Join</Link>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <Link to={`/creator/${user.id}`} className="flex items-center gap-3 border border-white/10 px-4 h-12 hover:bg-white/5 rounded-xl transition-colors" data-testid="mobile-nav-profile">
                  <User size={16} className="text-text2" />
                  <span className="font-mono text-sm truncate">{user.display_name}</span>
                  {user.is_verified && <BadgeCheck size={16} className="text-amber1" />}
                </Link>
                <Link to="/dashboard" className="font-display text-sm font-medium px-4 h-12 flex items-center gap-3 border border-white/5 hover:bg-white/5 rounded-xl transition-colors" data-testid="mobile-nav-dashboard">
                  <Store size={16} className="text-text2" /> My Shelf
                </Link>
                <Link to="/workflows" className="font-display text-sm font-medium px-4 h-12 flex items-center gap-3 border border-white/5 hover:bg-white/5 rounded-xl transition-colors" data-testid="mobile-nav-workflows">
                  <Zap size={16} className="text-text2" /> Workflows
                </Link>
                <Link to="/settings" className="font-display text-sm font-medium px-4 h-12 flex items-center gap-3 border border-white/5 hover:bg-white/5 rounded-xl transition-colors" data-testid="mobile-nav-settings">
                  <Settings size={16} className="text-text2" /> Settings
                </Link>
                {user.is_admin && (
                  <Link to="/moderation" className="font-display text-sm font-medium px-4 h-12 flex items-center gap-3 border border-white/5 hover:bg-white/5 text-tier_demo_tx rounded-xl transition-colors" data-testid="mobile-nav-moderation">
                    <Shield size={16} /> Moderation
                  </Link>
                )}
                <button
                  onClick={() => { logout(); setOpen(false); nav("/"); }}
                  className="font-display text-sm font-medium px-4 h-12 flex items-center gap-3 border border-white/5 hover:bg-white/5 text-text2 hover:text-text1 rounded-xl transition-colors"
                  data-testid="mobile-nav-logout"
                >
                  <LogOut size={16} /> Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
